<template>
  <div
    class="zt-select relative w-full cursor-pointer"
    :class="{
      'zt-select-outlined': outlined,
      'zt-select-focused': isFocused,
    }"
  >
    <div
      v-if="prependIcon"
      class="zt-select-prepended-icon flex justify-center"
    >
      <zt-icon :name="prependIcon" />
    </div>

    <input
      class="zt-select-input w-full cursor-pointer pl-0 pr-5"
      ref="ztInputSelect"
      :value="selectedValue"
      :class="{
        'zt-select-input-disabled': $attrs.disabled,
        'zt-select-input-prepended-icon': prependIcon.length,
      }"
      @click.stop
      placeholder=" "
      v-bind="$attrs" v-on="$listeners"
      @blur="_onBlur" @click.stop="_onClick"
      :id="componentId"
      :aria-owns="computedOwns"
      readonly
    />
    <label
      :for="componentId"
      class="zt-select-label pl-0"
      :class="{
        'zt-select-label-disabled': $attrs.disabled,
        'zt-select-label-prepended-icon': prependIcon.length,
      }"
    >
      {{ label }}
    </label>
    <div
      v-if="!outlined"
      class="zt-select-underline border-b border-zt-dark-500"
      :class="{ 'zt-select-underline-disabled': $attrs.disabled }"
    />
    <div
      class="zt-select-icon absolute text-lg"
      :class="[{ 'zt-input-icon-disabled': $attrs.disabled }]"
      @click.stop="_onClick"
    >
      <zt-icon name="caret-down" />
    </div>
    <!-- removed teleport -->
      <transition name="select-content">
        <div
          v-show="isActiveContent"
          ref="ztSelectOptions"
          class="zt-select-content absolute inline-block overflow-y-auto overflow-x-hidden rounded"
          :class="{
            'zt-select-content-outlined': outlined,
          }"
          :style="cordsContent"
          :id="computedOwns"
        >
          <div class="zt-select-list-box static block bg-white py-3">
            <template v-if="isGrouped">
              <div v-for="group in computedItems" :key="itemText">
                <div class="zt-select-group-title px-3 py-2 font-bold">
                  {{ group.name }}
                </div>
                <div>
                  <zt-option
                    v-for="(item, index) in group.childs"
                    :key="`option-${componentId}-${index}`"
                    :id="`option-${componentId}-${index}`"
                    :item="item"
                    :item-text="itemText"
                    :prepend-icon="
                      itemPrependIcon.length ? itemPrependIcon : ''
                    "
                    :item-group="true"
                    @select="optionSelected"
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <div>
                <zt-option
                  v-for="(item, index) in computedItems"
                  :key="index"
                  :id="`option-${componentId}-${index}`"
                  :item="item"
                  :item-text="itemText"
                  :prepend-icon="itemPrependIcon.length ? itemPrependIcon : ''"
                  @select="optionSelected"
                />
              </div>
            </template>
          </div>
        </div>
      </transition>
    <!-- /removed teleport -->
  </div>
</template>

<script>
import utils from '../../../../utils/helpers'
import ztOption from './ztOption.vue'
import ztIcon from '../ztIcon/ztIcon.vue'

export default {
  name: 'ztSelect',
  inheritAttrs: false,
  components: { ztOption, ztIcon },
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: [String, Array],
      default: '',
    },
    itemValue: {
      type: [String, Array],
      default: '',
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: '',
    },
    itemPrependIcon: {
      type: String,
      default: '',
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    alternativeItems: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      componentId: '',
      isActiveContent: false,
      isFocused: false,
      cordsContent: {},
      itemSelected: {},
      selectedValue: ''
    }
  },
  provide() {
    return {
      selectedValue: this.selectedValue
    }
  },
  computed: {
    isGrouped() {
      return this.grouped && Array.isArray(this.items)
    },
    computedOwns() {
      return `list-${this.componentId}`
    },
    computedItems() {
      let arr = this.alternativeItems && this.alternativeItems.length ? this.alternativeItems.concat(this.items) : this.items;
      return this.generateItemsMap(arr)
    },
    isValueExist() {
      return this.computedItems.some((item) => {
        if (this.returnObject)
          return this.getValueObject(this.value) === this.getValueObject(item)
        else return this.value === this.getTextObject(item)
      })
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.setValue(newVal)
      },
      immediate: true
    },
    isActiveContent(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.cordsContent = this.positionContent()
          window.addEventListener('scroll', this.handleReposition, true)
          window.addEventListener('resize', this.handleReposition)
        })
      } else {
        window.removeEventListener('scroll', this.handleReposition, true)
        window.removeEventListener('resize', this.handleReposition)
      }
    }
  },
  mounted() {
    this.componentId = Date.now().toString()
    this.setValue(this.value)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleReposition, true)
    window.removeEventListener('resize', this.handleReposition)
    document.removeEventListener('click', this.clickBlur)
  },
  methods: {
    handleReposition() {
      this.cordsContent = this.positionContent()
    },
    setValue(value) {
      if (!value) {
        this.selectedValue = ''
        return
      }
      return this.isValueExist
        ? (this.selectedValue = this.returnObject ? this.getTextObject(value) : value)
        : (this.selectedValue = '')
    },
    _onClick(event) {
      this.$emit('focus', event)
      this.isActiveContent = true
      document.addEventListener('click', this.clickBlur)
      this.$nextTick(() => {
        this.cordsContent = this.positionContent()
      })
    },
    _onBlur(event) {
      if (
        event.relatedTarget
          ? !event.relatedTarget.closest('.zt-select-content')
          : false
      ) {
        this.closeContent()
      }
      this.isFocused = false
      this.$emit('blur', event)
    },
    clickBlur(event) {
      let optionsContent = event.target.closest('.zt-select-content')
      if (!optionsContent) {
        this.closeContent()
        this.positionContent()
      }
    },
    closeContent() {
      this.isActiveContent = false
      document.removeEventListener('click', this.clickBlur)
    },
    positionContent() {
      let inputSelect = this.$refs.ztInputSelect
      let selectOptions = this.$refs.ztSelectOptions
      if(!inputSelect || !selectOptions) return {}
      let topPos = 0
      let leftPos = 0
      let widthContent = 0
      let scrollContentTop =
        window.pageYOffset || document.documentElement.scrollTop
      if (
        inputSelect.getBoundingClientRect().top +
          selectOptions.getBoundingClientRect().height >=
          window.innerHeight ||
        (this.outlined &&
          inputSelect.getBoundingClientRect().top +
            selectOptions.getBoundingClientRect().height +
            inputSelect.clientHeight >=
            window.innerHeight)
      ) {
        topPos =
          inputSelect.getBoundingClientRect().top +
          inputSelect.clientHeight +
          scrollContentTop -
          selectOptions.getBoundingClientRect().height

        if (this.outlined) topPos = topPos - (inputSelect.clientHeight + 1)
      } else {
        topPos = inputSelect.getBoundingClientRect().top + scrollContentTop
        if (this.outlined) topPos = topPos + (inputSelect.clientHeight + 1)
      }
      leftPos = inputSelect.getBoundingClientRect().left
      widthContent = inputSelect.offsetWidth
      if (this.outlined || this.prependIcon) {
        const style = getComputedStyle(inputSelect)
        widthContent =
          inputSelect.offsetWidth +
          parseInt(style.marginLeft) +
          parseInt(style.marginRight)
        leftPos = leftPos - parseInt(style.marginLeft)
      }
      return {
        left: `${leftPos}px`,
        top: `${topPos}px`,
        width: `${widthContent}px`,
        zIndex: 8,
      }
    },
    generateItemsMap(items) {
      const itemsMaps = new Map()
      for (let index = 0; index < items.length; index++) {
        const item = items[index]
        if (typeof item === 'object') {
          const val = this.getTextObject(item)
          !itemsMaps.has(val) && itemsMaps.set(val, item)
        } else {
          const itemObj = { value: index, text: item }
          !itemsMaps.has(item) && itemsMaps.set(item, itemObj)
        }
      }
      return Array.from(itemsMaps.values())
    },
    getTextObject(item) {
      return this.itemText === ''
        ? utils.getPropertyItem(item, 'text')
        : utils.getPropertyItem(item, this.itemText)
    },
    getValueObject(item) {
      return this.itemText === ''
        ? utils.getPropertyItem(item, 'value')
        : utils.getPropertyItem(item, this.itemValue)
    },
    optionSelected(item) {
      this.closeContent()
      this.itemSelected = item
      this.selectedValue = this.getTextObject(item)
      this.returnObject
        ? this.$emit('input', this.itemSelected)
        : this.$emit('input', this.getTextObject(item))
      this.$emit('change', this.itemSelected)
    }
  }
}
</script>
